import { GEO_CORDS_MATCH_PRECISION } from "../constants";
import type { IStationInfo } from "../dto";
import { DirectionEnum } from "../enums";
import type {
  IAreaWaypoint,
  IAreaWaypointsByDirection,
  IRemovedAreaWaypoint,
  TAreaWaypointDirection,
} from "../models/area.models";
import { isAreaRoutingWaypoint } from "./routing-waypoint/routing-waypoint.helpers";
import { generateStationRouteWaypointId } from "./station-route";

export const emptyAreaWaypointsByDirection = (): IAreaWaypointsByDirection => ({
  [DirectionEnum.PickUp]: [],
  [DirectionEnum.DropOff]: [],
});

export const normalizeAreaWaypointsByDirection = (
  value?: IAreaWaypointsByDirection | null,
): IAreaWaypointsByDirection => ({
  [DirectionEnum.PickUp]: [...(value?.[DirectionEnum.PickUp] ?? [])],
  [DirectionEnum.DropOff]: [...(value?.[DirectionEnum.DropOff] ?? [])],
});

export const getAreaWaypointsForDirection = ({
  areaWaypoints,
  direction,
}: {
  areaWaypoints: IAreaWaypointsByDirection | undefined;
  direction: TAreaWaypointDirection;
}): IAreaWaypoint[] => {
  if (!areaWaypoints) {
    return [];
  }

  return areaWaypoints[direction] ?? [];
};

export const setAreaWaypointsForDirection = ({
  areaWaypoints,
  direction,
  waypoints,
}: {
  areaWaypoints: IAreaWaypointsByDirection | undefined;
  direction: TAreaWaypointDirection;
  waypoints: IAreaWaypoint[];
}): IAreaWaypointsByDirection => {
  const normalized = normalizeAreaWaypointsByDirection(areaWaypoints);
  normalized[direction] = waypoints;
  return normalized;
};

export const flattenAreaWaypoints = (
  areaWaypoints?: IAreaWaypointsByDirection | null,
): IAreaWaypoint[] => [
  ...(areaWaypoints?.[DirectionEnum.PickUp] ?? []),
  ...(areaWaypoints?.[DirectionEnum.DropOff] ?? []),
];

export const stampAreaIdOnAreaWaypoints = ({
  areaWaypoints,
  areaId,
}: {
  areaWaypoints: IAreaWaypointsByDirection | undefined;
  areaId: number;
}): IAreaWaypointsByDirection => {
  const stamp = (waypoints: IAreaWaypoint[] | undefined): IAreaWaypoint[] =>
    (waypoints ?? []).map(waypoint => ({ ...waypoint, areaId }));

  return {
    [DirectionEnum.PickUp]: stamp(areaWaypoints?.[DirectionEnum.PickUp]),
    [DirectionEnum.DropOff]: stamp(areaWaypoints?.[DirectionEnum.DropOff]),
  };
};

export const createAreaWaypoint = ({
  name,
  latitude,
  longitude,
  fullAddress,
  id,
  areaId,
}: {
  name: string;
  latitude: number;
  longitude: number;
  fullAddress?: string;
  id?: number;
  areaId?: number;
}): IAreaWaypoint => ({
  id: id ?? generateStationRouteWaypointId(),
  name,
  latitude,
  longitude,
  fullAddress,
  ...(areaId ? { areaId } : {}),
});

export const areaWaypointReactKey = (
  direction: TAreaWaypointDirection,
  waypointId: number,
): string => `${direction}:${waypointId}`;

export const isSameAreaWaypointLocation = (
  left: Pick<IAreaWaypoint, "latitude" | "longitude">,
  right: Pick<IAreaWaypoint, "latitude" | "longitude">,
): boolean =>
  left.latitude.toPrecision(GEO_CORDS_MATCH_PRECISION) ===
    right.latitude.toPrecision(GEO_CORDS_MATCH_PRECISION) &&
  left.longitude.toPrecision(GEO_CORDS_MATCH_PRECISION) ===
    right.longitude.toPrecision(GEO_CORDS_MATCH_PRECISION);

export const isAreaWaypointAlreadySelected = (
  waypoints: IAreaWaypoint[],
  candidate: Pick<IAreaWaypoint, "id" | "latitude" | "longitude">,
): boolean =>
  waypoints.some(
    waypoint =>
      waypoint.id === candidate.id ||
      isSameAreaWaypointLocation(waypoint, candidate),
  );

/** Area default W row → the record that keeps it off the ride once deleted; undefined for any other row. */
export const toRemovedAreaWaypoint = (
  station: Pick<IStationInfo, "id" | "areaId" | "waypointCategory"> | undefined,
): IRemovedAreaWaypoint | undefined => {
  if (!station?.areaId || !isAreaRoutingWaypoint(station)) {
    return undefined;
  }

  return {
    areaId: station.areaId,
    waypointId: station.id,
  } satisfies IRemovedAreaWaypoint;
};

export const appendRemovedAreaWaypoint = ({
  removedAreaWaypoints,
  removedAreaWaypoint,
}: {
  removedAreaWaypoints: IRemovedAreaWaypoint[] | undefined;
  removedAreaWaypoint: IRemovedAreaWaypoint;
}): IRemovedAreaWaypoint[] => {
  const current = removedAreaWaypoints ?? [];
  const isAlreadyRemoved = current.some(
    removed =>
      removed.areaId === removedAreaWaypoint.areaId &&
      removed.waypointId === removedAreaWaypoint.waypointId,
  );

  return isAlreadyRemoved ? current : [...current, removedAreaWaypoint];
};
